// overviewQueries — pure data layer for the Overview dashboard.
//
// Every metric on the dashboard traces to a real COUNT/SELECT against a
// Reverra table. No fabricated "time saved" or fake percentages — every
// number is reproducible by paste-and-run SQL.
//
// Pattern: PostgREST HEAD-count requests (`{ count: 'exact', head: true }`)
// so the wire only carries the count, not the rows. RLS-scoped to the
// authenticated owner via the Supabase session.
//
// 2026-07-02 definition audit (dashboard-overhaul):
//   - CAPTURE_TABLES: dropped `shopify_orders` — the Shopify integration was
//     removed from Connections (PR #118); counting its rows inflated
//     "Captured" with a dead channel.
//   - "Handled" redefined: it used to count rows CREATED in agent-output
//     tables (approvals, digest_entries, …) — i.e. agent busyness, not things
//     the owner actually handled. It now counts OWNER DECISIONS: approvals
//     decided today (decided_at) + action items completed today
//     (status='done', updated_at) — see loadHandledStats.
//   - Review-queue counts unified + orphan-proof: agreements and commissions
//     count from their CANONICAL tables (matching the Agreements/Commissions
//     pages) instead of pending `approvals` rows, so a stale approval whose
//     target was soft-deleted (two real orphans found on reverra-dev
//     2026-07-02: one commission_export → soft-deleted commission, one
//     agreement_redline → 'mock-agreement-*' seed id) can no longer make the
//     dashboard disagree with the page it links to.

import type { SupabaseClient } from '@supabase/supabase-js';

/** Tables that represent INGEST (system "captured" something from the world). */
export const CAPTURE_TABLES = [
  'messages',
  'emails',
  'calls',
  'meetings',
  'calendar_events',
] as const;

/**
 * Returns the ISO timestamp of "today 00:00 in PT". Joel + the demo data
 * both live in PT; we anchor the dashboard's day-boundary there so
 * "Captured today" doesn't shift to "yesterday" overnight UTC.
 */
export function todayStartPtIso(now: Date = new Date()): string {
  // PT is UTC-8 (PST) or UTC-7 (PDT). We compute "today in PT" by
  // formatting in en-CA (yields YYYY-MM-DD), then concatenating
  // T00:00:00 + a tz offset. The Intl path picks the right DST.
  const pt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  const ptMidnightUtc = new Date(`${pt}T00:00:00-07:00`); // PDT (May–Oct)
  const checkPdt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(ptMidnightUtc);
  if (checkPdt === pt) return ptMidnightUtc.toISOString();
  return new Date(`${pt}T00:00:00-08:00`).toISOString();
}

/** Returns the ISO timestamp 7 days before `todayStartPtIso(now)`. */
export function sevenDaysAgoStartPtIso(now: Date = new Date()): string {
  const today = new Date(todayStartPtIso(now));
  today.setUTCDate(today.getUTCDate() - 7);
  return today.toISOString();
}

/** Returns the ISO timestamp `days` days before `todayStartPtIso(now)`. */
export function daysAgoStartPtIso(days: number, now: Date = new Date()): string {
  const today = new Date(todayStartPtIso(now));
  today.setUTCDate(today.getUTCDate() - days);
  return today.toISOString();
}

/** The PT calendar-day key (YYYY-MM-DD) for an instant. */
export function ptDayKey(iso: string | Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(typeof iso === 'string' ? new Date(iso) : iso);
}

/** Result shape for one window's counts across the listed tables. */
export type WindowCounts = {
  total: number;
  byTable: Record<string, number>;
};

/**
 * Count rows created in each table within [start, end). Uses HEAD-count
 * requests in parallel; a permission-denied (RLS) on one table is logged
 * via the per-table `byTable` value of 0 — the page still renders.
 */
async function countWindow(
  supabase: SupabaseClient,
  tables: readonly string[],
  startIso: string,
  endIso?: string,
): Promise<WindowCounts> {
  const byTable: Record<string, number> = {};
  await Promise.all(
    tables.map(async (table) => {
      let q = supabase
        .from(table)
        .select('id', { count: 'exact', head: true })
        .gte('created_at', startIso);
      if (endIso) q = q.lt('created_at', endIso);
      const { count, error } = await q;
      if (error) {
        console.warn(`overviewQueries: count for ${table} failed — ${error.message}`);
      }
      byTable[table] = error ? 0 : (count ?? 0);
    }),
  );
  const total = Object.values(byTable).reduce((acc, n) => acc + n, 0);
  return { total, byTable };
}

/** Returns today's capture counts + the trailing-7-day per-day average. */
export async function loadCaptureStats(
  supabase: SupabaseClient,
  now: Date = new Date(),
): Promise<{ today: WindowCounts; sevenDayAvgPerDay: number }> {
  const todayStart = todayStartPtIso(now);
  const sevenDaysAgo = sevenDaysAgoStartPtIso(now);
  const [today, week] = await Promise.all([
    countWindow(supabase, CAPTURE_TABLES, todayStart),
    countWindow(supabase, CAPTURE_TABLES, sevenDaysAgo, todayStart),
  ]);
  return { today, sevenDayAvgPerDay: week.total / 7 };
}

/**
 * "Handled" = things the OWNER dealt with: approvals they decided
 * (decided_at in window — any target_type, any decision) + action items
 * marked done (status='done', updated_at in window; the table has no
 * completed_at, so updated_at is the closest honest proxy — a later edit
 * to a done item re-dates it, which we accept and document).
 */
async function countHandledWindow(
  supabase: SupabaseClient,
  startIso: string,
  endIso?: string,
): Promise<WindowCounts> {
  const byTable: Record<string, number> = {};

  let approvalsQ = supabase
    .from('approvals')
    .select('id', { count: 'exact', head: true })
    .gte('decided_at', startIso);
  if (endIso) approvalsQ = approvalsQ.lt('decided_at', endIso);

  let itemsQ = supabase
    .from('meeting_action_items')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'done')
    .gte('updated_at', startIso);
  if (endIso) itemsQ = itemsQ.lt('updated_at', endIso);

  const [approvalsRes, itemsRes] = await Promise.all([approvalsQ, itemsQ]);
  if (approvalsRes.error) {
    console.warn(`overviewQueries: handled approvals count failed — ${approvalsRes.error.message}`);
  }
  if (itemsRes.error) {
    console.warn(`overviewQueries: handled action-items count failed — ${itemsRes.error.message}`);
  }
  byTable.approvals_decided = approvalsRes.error ? 0 : (approvalsRes.count ?? 0);
  byTable.action_items_done = itemsRes.error ? 0 : (itemsRes.count ?? 0);
  const total = byTable.approvals_decided + byTable.action_items_done;
  return { total, byTable };
}

/** Returns today's owner-handled counts + the trailing-7d per-day average. */
export async function loadHandledStats(
  supabase: SupabaseClient,
  now: Date = new Date(),
): Promise<{ today: WindowCounts; sevenDayAvgPerDay: number }> {
  const todayStart = todayStartPtIso(now);
  const sevenDaysAgo = sevenDaysAgoStartPtIso(now);
  const [today, week] = await Promise.all([
    countHandledWindow(supabase, todayStart),
    countHandledWindow(supabase, sevenDaysAgo, todayStart),
  ]);
  return { today, sevenDayAvgPerDay: week.total / 7 };
}

// ============================================================================
// Review queue — the "Needs your eyes" counts, one definition per vertical,
// each matching the page the card links to (canonical + orphan-proof).
// ============================================================================

/** Approval target_type literals written by the agents — locked by tests.
 *  NEVER make up a target_type that doesn't appear in the migrations'
 *  CHECK constraint — the query silently returns 0 and the UI is wrong. */
const APPROVAL_TARGET_TYPES = {
  email: 'gmail_thread',
  focusReschedule: 'focus_reschedule',
} as const;

export type ReviewQueueCounts = {
  /** approvals(target_type='gmail_thread', status='pending') — matches /inbox. */
  emailDrafts: number;
  /** approvals(target_type='focus_reschedule', status='pending') — matches /focus. */
  focusReschedules: number;
  /** agreements(status='under_review', not deleted) — matches /agreements. */
  agreementRedlines: number;
  /** commissions(processing_status IN validated|awaiting_approval, not deleted) — matches /commissions. */
  commissionsAwaitingApproval: number;
  /** commissions(processing_status='approved', gusto_export_path IS NULL, not deleted). */
  commissionsToExport: number;
  /** Sum of the four review-needing counts (commissionsToExport excluded — that's post-approval). */
  total: number;
};

export async function loadReviewQueue(supabase: SupabaseClient): Promise<ReviewQueueCounts> {
  const pendingApprovals = async (targetType: string): Promise<number> => {
    const { count, error } = await supabase
      .from('approvals')
      .select('id', { count: 'exact', head: true })
      .eq('target_type', targetType)
      .eq('status', 'pending');
    return error ? 0 : (count ?? 0);
  };
  const agreementsUnderReview = async (): Promise<number> => {
    const { count, error } = await supabase
      .from('agreements')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'under_review')
      .is('deleted_at', null);
    return error ? 0 : (count ?? 0);
  };
  const commissionsAwaiting = async (): Promise<number> => {
    const { count, error } = await supabase
      .from('commissions')
      .select('id', { count: 'exact', head: true })
      .in('processing_status', ['validated', 'awaiting_approval'])
      .is('deleted_at', null);
    return error ? 0 : (count ?? 0);
  };
  const commissionsExportable = async (): Promise<number> => {
    const { count, error } = await supabase
      .from('commissions')
      .select('id', { count: 'exact', head: true })
      .eq('processing_status', 'approved')
      .is('gusto_export_path', null)
      .is('deleted_at', null);
    return error ? 0 : (count ?? 0);
  };

  const [
    emailDrafts,
    focusReschedules,
    agreementRedlines,
    commissionsAwaitingApproval,
    commissionsToExport,
  ] = await Promise.all([
    pendingApprovals(APPROVAL_TARGET_TYPES.email),
    pendingApprovals(APPROVAL_TARGET_TYPES.focusReschedule),
    agreementsUnderReview(),
    commissionsAwaiting(),
    commissionsExportable(),
  ]);

  return {
    emailDrafts,
    focusReschedules,
    agreementRedlines,
    commissionsAwaitingApproval,
    commissionsToExport,
    total: emailDrafts + focusReschedules + agreementRedlines + commissionsAwaitingApproval,
  };
}

// ============================================================================
// Capture series — per-PT-day counts for the sparkline. Real created_at
// buckets, zero-filled, oldest → newest.
// ============================================================================

export type CaptureSeries = {
  /** PT day keys (YYYY-MM-DD), oldest first, length = days. */
  days: string[];
  /** Row count per day, aligned with `days`. */
  counts: number[];
  total: number;
};

/** Pure: bucket ISO timestamps into the last `days` PT calendar days. */
export function bucketByPtDay(
  isoDates: string[],
  days: number,
  now: Date = new Date(),
): CaptureSeries {
  const keys: string[] = [];
  const todayStart = new Date(todayStartPtIso(now));
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(todayStart);
    d.setUTCDate(d.getUTCDate() - i);
    // todayStart is PT-midnight expressed in UTC; adding 12h keeps us safely
    // inside the intended PT day regardless of DST for the key format.
    const mid = new Date(d.getTime() + 12 * 3600 * 1000);
    keys.push(ptDayKey(mid));
  }
  const counts = new Array<number>(keys.length).fill(0);
  const index = new Map(keys.map((k, i) => [k, i]));
  let total = 0;
  for (const iso of isoDates) {
    const i = index.get(ptDayKey(iso));
    if (i !== undefined) {
      counts[i] += 1;
      total += 1;
    }
  }
  return { days: keys, counts, total };
}

/** PostgREST page size. MUST NOT exceed supabase/config.toml `max_rows`
 *  (1000): the server truncates any single response to max_rows, and this
 *  paging loop detects exhaustion via a short page — a page size above the
 *  server cap would false-terminate after one page (the exact silent-
 *  truncation failure documented in docs/runbooks/gusto-export.md). */
const SERIES_PAGE_SIZE = 1000;
/** Per-table safety cap across pages — bounds the transfer if a channel
 *  explodes; the sparkline then undercounts WITH a console.warn. */
export const SERIES_ROW_CAP = 3000;

/** Fetch created_at for every row in [start, now] — PAGED via .range()
 *  because a single request is server-capped at max_rows=1000. Exported
 *  for the scaling regression test (reviewer R1 2026-07-02 Blocker). */
export async function fetchSeriesDates(
  supabase: SupabaseClient,
  table: string,
  startIso: string,
  cap: number = SERIES_ROW_CAP,
): Promise<string[]> {
  const dates: string[] = [];
  for (let from = 0; from < cap; from += SERIES_PAGE_SIZE) {
    const to = Math.min(from + SERIES_PAGE_SIZE, cap) - 1;
    const { data, error } = await supabase
      .from(table)
      .select('created_at')
      .gte('created_at', startIso)
      .order('created_at', { ascending: true })
      .range(from, to);
    if (error) {
      console.warn(`overviewQueries: series for ${table} failed — ${error.message}`);
      return dates;
    }
    const rows = (data as Array<{ created_at: string }>) ?? [];
    for (const r of rows) dates.push(r.created_at);
    // Short page ⇒ table exhausted (page size ≤ server max_rows makes this
    // reliable). Full final page at the cap ⇒ we truncated: warn loudly.
    if (rows.length < to - from + 1) return dates;
  }
  console.warn(
    `overviewQueries: series for ${table} hit the ${cap}-row cap — sparkline undercounts`,
  );
  return dates;
}

export async function loadCaptureSeries(
  supabase: SupabaseClient,
  now: Date = new Date(),
  days = 14,
): Promise<CaptureSeries> {
  const start = daysAgoStartPtIso(days - 1, now);
  const allDates: string[] = [];
  await Promise.all(
    CAPTURE_TABLES.map(async (table) => {
      const dates = await fetchSeriesDates(supabase, table, start);
      allDates.push(...dates);
    }),
  );
  return bucketByPtDay(allDates, days, now);
}

// ============================================================================
// Recent activity — the live pulse feed. Small per-table fetches merged +
// sorted client-side; every row is a real DB record.
// ============================================================================

export type ActivityEvent = {
  id: string;
  kind: 'email' | 'message' | 'meeting' | 'decision';
  title: string;
  detail: string;
  atIso: string;
};

/** Pure: merge event lists newest-first, capped at `limit`. Exported for tests. */
export function mergeActivity(events: ActivityEvent[], limit: number): ActivityEvent[] {
  return [...events]
    .sort((a, b) => new Date(b.atIso).getTime() - new Date(a.atIso).getTime())
    .slice(0, limit);
}

export async function loadRecentActivity(
  supabase: SupabaseClient,
  limit = 8,
): Promise<ActivityEvent[]> {
  const events: ActivityEvent[] = [];

  const [emailsRes, messagesRes, meetingsRes, decisionsRes] = await Promise.all([
    supabase
      .from('emails')
      .select('id, subject, from_name, from_email, created_at')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(6),
    supabase
      .from('messages')
      .select('id, source_channel, counterparty_name, counterparty_handle, created_at')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(6),
    supabase
      .from('meetings')
      .select('id, title, created_at')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(4),
    supabase
      .from('approvals')
      .select('id, target_type, status, decided_at')
      .not('decided_at', 'is', null)
      .order('decided_at', { ascending: false })
      .limit(5),
  ]);

  if (!emailsRes.error) {
    for (const r of (emailsRes.data as Array<{
      id: string;
      subject: string | null;
      from_name: string | null;
      from_email: string | null;
      created_at: string;
    }>) ?? []) {
      events.push({
        id: `email-${r.id}`,
        kind: 'email',
        title: r.subject?.trim() || '(no subject)',
        detail: r.from_name || r.from_email || 'Unknown sender',
        atIso: r.created_at,
      });
    }
  }
  if (!messagesRes.error) {
    for (const r of (messagesRes.data as Array<{
      id: string;
      source_channel: string;
      counterparty_name: string | null;
      counterparty_handle: string | null;
      created_at: string;
    }>) ?? []) {
      events.push({
        id: `message-${r.id}`,
        kind: 'message',
        title: r.counterparty_name || r.counterparty_handle || 'Unknown contact',
        detail: r.source_channel,
        atIso: r.created_at,
      });
    }
  }
  if (!meetingsRes.error) {
    for (const r of (meetingsRes.data as Array<{
      id: string;
      title: string | null;
      created_at: string;
    }>) ?? []) {
      events.push({
        id: `meeting-${r.id}`,
        kind: 'meeting',
        title: r.title?.trim() || 'Meeting captured',
        detail: 'Fathom transcript',
        atIso: r.created_at,
      });
    }
  }
  if (!decisionsRes.error) {
    for (const r of (decisionsRes.data as Array<{
      id: string;
      target_type: string;
      status: string;
      decided_at: string;
    }>) ?? []) {
      events.push({
        id: `decision-${r.id}`,
        kind: 'decision',
        title: decisionTitle(r.status, r.target_type),
        detail: 'Approval decision',
        atIso: r.decided_at,
      });
    }
  }

  return mergeActivity(events, limit);
}

/** Pure: feed copy for an approval decision, with the right article
 *  ("an email draft", "a focus reschedule"). Exported for tests —
 *  reviewer R1 2026-07-02 caught "You approved a email draft". */
export function decisionTitle(status: string, targetType: string): string {
  const typeLabel: Record<string, string> = {
    gmail_thread: 'an email draft',
    focus_reschedule: 'a focus reschedule',
    agreement_redline: 'an agreement redline',
    commission_export: 'a commission export',
  };
  const label = typeLabel[targetType] ?? `a ${targetType.replace(/_/g, ' ')}`;
  return `You ${status} ${label}`;
}

// ============================================================================
// Pure display helpers
// ============================================================================

/** Honest caption comparing a day's value to its trailing average. */
export function activityCaption(value: number, average: number): string {
  if (average <= 0) return value === 0 ? 'Nothing yet today' : 'New activity today';
  const ratio = value / average;
  if (ratio >= 1.25) return 'Above average';
  if (ratio >= 0.75) return 'Typical day';
  if (ratio > 0) return 'Quieter than usual';
  return 'Nothing yet today';
}

/** Signed percent delta vs the average, or null when there's no baseline. */
export function trendDeltaPct(value: number, average: number): number | null {
  if (average <= 0) return null;
  return Math.round(((value - average) / average) * 100);
}

/** Compact relative-time label ("just now", "4m ago", "2h ago", "3d ago"). */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const ms = now.getTime() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Greeting based on the hour in PT. Pure helper — exported for testability. */
export function greetingForHour(
  now: Date = new Date(),
): 'Good morning' | 'Good afternoon' | 'Good evening' {
  const hour = Number(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: '2-digit',
      hour12: false,
    }).format(now),
  );
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/** Today's date label for the hero subtitle. PT-anchored. */
export function todayLabel(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(now);
}
